/* ==========================================================================
   Lalla — product.js
   <product-info>     variant selection, availability, price, inventory,
                      URL sync, add to cart / COD checkout, sticky bar.
   <media-gallery>    scroll-snap gallery, arrows, thumbnails, zoom.
   <deferred-media>   external videos & 3D models loaded on demand.
   Depends on theme.js (window.Lalla).
   ========================================================================== */

(() => {
  if (customElements.get('product-info')) return;

  const L = window.Lalla;
  const { qs, qsa } = L;
  const desktop = window.matchMedia('(min-width: 990px)');
  const RECENT_KEY = 'lalla:recently-viewed';

  const readJSON = (el) => {
    try {
      return el ? JSON.parse(el.textContent) : null;
    } catch (error) {
      console.error('product.js: invalid JSON', error);
      return null;
    }
  };

  /* ------------------------------------------------------------------------
     <media-gallery>
     ------------------------------------------------------------------------ */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = qs('[data-gallery-track]', this);
      if (!this.track) return;
      this.slides = qsa('.gallery__slide', this);
      this.thumbs = qsa('[data-thumb-index]', this);
      this.prev = qs('[data-gallery-prev]', this);
      this.next = qs('[data-gallery-next]', this);
      this.current = qs('[data-gallery-current]', this);
      this.status = qs('[data-gallery-status]', this);
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));

      this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
      this.next?.addEventListener('click', () => this.goTo(this.index + 1));
      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex)))
      );

      // Track the slide in view while swiping.
      this.track.addEventListener('scroll', L.debounce(() => this.syncFromScroll(), 60), { passive: true });

      // Zoom
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-zoom-index]');
        if (trigger) this.openZoom(Number(trigger.dataset.zoomIndex), trigger);
      });

      if (this.index > 0) this.goTo(this.index, { instant: true });
      else this.setActive(0);
    }

    get isGrid() {
      return this.dataset.layout === 'grid' && desktop.matches;
    }

    syncFromScroll() {
      if (this.isGrid) return;
      const trackLeft = this.track.getBoundingClientRect().left;
      let closest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, i) => {
        const d = Math.abs(slide.getBoundingClientRect().left - trackLeft - this.paddingStart());
        if (d < distance) {
          distance = d;
          closest = i;
        }
      });
      if (closest !== this.index) this.setActive(closest);
    }

    paddingStart() {
      return parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
    }

    goTo(index, { instant = false } = {}) {
      if (!this.slides.length) return;
      const i = Math.max(0, Math.min(index, this.slides.length - 1));
      const slide = this.slides[i];
      if (!this.isGrid) {
        // Scroll the track only (never the page).
        const left = slide.offsetLeft - this.slides[0].offsetLeft;
        this.track.scrollTo({ left, behavior: instant || L.prefersReducedMotion() ? 'auto' : 'smooth' });
      }
      this.setActive(i);
    }

    goToMedia(mediaId) {
      if (!mediaId) return;
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide) return;
      if (this.isGrid) {
        // Editorial grid: bring the variant image to the first position.
        this.track.prepend(slide);
        this.slides = qsa('.gallery__slide', this);
        this.setActive(0);
        return;
      }
      this.goTo(this.slides.indexOf(slide));
    }

    setActive(i) {
      this.index = i;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      this.thumbs.forEach((t, n) => {
        if (n === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (this.current) this.current.textContent = String(i + 1);
      if (this.status && this.dataset.counterTemplate !== undefined) {
        this.status.textContent = this.dataset.counterTemplate.replace('[index]', i + 1).replace('[count]', this.slides.length);
      }
      this.prev?.setAttribute('aria-disabled', String(i === 0));
      this.next?.setAttribute('aria-disabled', String(i === this.slides.length - 1));
      // Pause videos that are no longer visible.
      qsa('video', this).forEach((video) => {
        if (!this.slides[i].contains(video)) video.pause();
      });
    }

    openZoom(index, trigger) {
      const modal = document.getElementById(`ZoomDialog-${this.dataset.section}`);
      if (!modal || typeof modal.open !== 'function') return;
      modal.open(trigger);
      const item = qs(`[data-zoom-item="${index}"]`, modal);
      if (item) requestAnimationFrame(() => item.scrollIntoView({ block: 'start' }));
    }
  }

  customElements.define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------------
     <deferred-media> — external video / 3D model loaded on click
     ------------------------------------------------------------------------ */

  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      const trigger = qs('[data-deferred-trigger]', this);
      trigger?.addEventListener('click', () => this.load(), { once: true });
    }

    load() {
      const template = qs('template', this);
      if (!template) return;
      const content = template.content.cloneNode(true);
      this.replaceChildren(content);
      this.classList.add('is-loaded');
      const model = qs('model-viewer', this);
      if (model && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: (errors) => {
              if (errors || !window.Shopify.ModelViewerUI) return;
              new window.Shopify.ModelViewerUI(model);
            },
          },
        ]);
      }
      const focusable = qs('iframe, video, model-viewer', this);
      if (focusable) focusable.focus?.();
    }
  }

  customElements.define('deferred-media', DeferredMedia);

  /* ------------------------------------------------------------------------
     <sticky-buy-bar> — visibility handled by <product-info>
     ------------------------------------------------------------------------ */

  class StickyBuyBar extends HTMLElement {
    show(visible) {
      if (this.visible === visible) return;
      this.visible = visible;
      this.classList.toggle('is-visible', visible);
      this.toggleAttribute('inert', !visible);
      this.setAttribute('aria-hidden', String(!visible));
      document.documentElement.classList.toggle('has-sticky-buy', visible);
    }
  }

  customElements.define('sticky-buy-bar', StickyBuyBar);

  /* ------------------------------------------------------------------------
     <product-info>
     ------------------------------------------------------------------------ */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.sectionId = this.dataset.section;
      this.product = readJSON(qs('[data-product-data]', this));
      this.strings = readJSON(qs('[data-product-strings]', this)) || {};
      this.inventorySettings = readJSON(qs('[data-inventory-settings]', this)) || { enabled: false };
      if (!this.product) return;

      this.picker = qs('[data-variant-picker]', this);
      this.fieldsets = qsa('fieldset[data-option-index]', this);
      this.form = document.getElementById(`ProductForm-${this.sectionId}`);
      this.idInput = this.form ? qs('[data-variant-id]', this.form) : null;
      this.submitButtons = this.form ? qsa('[data-submit-action]', this.form) : [];
      this.gallery = qs('media-gallery', this);
      this.sticky = document.getElementById(`StickyBuy-${this.sectionId}`);

      const counter = qs('[data-gallery-status]', this);
      if (counter && this.gallery) this.gallery.dataset.counterTemplate = this.strings.mediaCounter || '';

      this.selected = this.fieldsets.map((fieldset) => {
        const checked = qs('input:checked', fieldset);
        return checked ? checked.value : null;
      });

      this.picker?.addEventListener('change', (event) => this.onOptionChange(event));
      this.form?.addEventListener('submit', (event) => this.onSubmit(event));

      this.refreshAvailability();
      this.update({ initial: true });
      this.setupSticky();
      if (this.dataset.trackRecent !== 'false') this.rememberProduct();
    }

    /* ---------- Variant state ---------- */

    get variants() {
      return this.product.variants;
    }

    currentVariant() {
      if (this.selected.some((v) => v === null)) return null;
      return this.variants.find((variant) => variant.options.every((value, i) => value === this.selected[i])) || null;
    }

    /* Best match for display while the selection is incomplete. */
    displayVariant() {
      const matching = this.variants.filter((variant) =>
        variant.options.every((value, i) => this.selected[i] === null || this.selected[i] === value)
      );
      return matching.find((v) => v.available) || matching[0] || this.variants[0];
    }

    onOptionChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      const fieldset = input.closest('fieldset[data-option-index]');
      const index = Number(fieldset.dataset.optionIndex);
      this.selected[index] = input.value;
      this.clearOptionError(fieldset);

      this.refreshAvailability();

      // Later options that became impossible are deselected — never swapped
      // silently for another value. The customer is told what to re-select.
      for (let i = index + 1; i < this.fieldsets.length; i++) {
        const value = this.selected[i];
        if (value === null) continue;
        const radio = qsa('input', this.fieldsets[i]).find((r) => r.value === value);
        if (radio && radio.disabled) {
          radio.checked = false;
          this.selected[i] = null;
          L.announce(
            (this.strings.optionReset || '')
              .replace('[value]', value)
              .replace('[option]', this.fieldsets[i].dataset.optionName)
          );
        }
      }

      this.update();
    }

    /* Availability of option N depends on the selections before it. */
    refreshAvailability() {
      this.fieldsets.forEach((fieldset, index) => {
        qsa('input[type="radio"]', fieldset).forEach((radio) => {
          const candidates = this.variants.filter(
            (variant) =>
              variant.options[index] === radio.value &&
              this.selected.slice(0, index).every((sel, i) => sel === null || variant.options[i] === sel)
          );
          const exists = candidates.length > 0;
          const available = candidates.some((variant) => variant.available);
          const label = radio.nextElementSibling;
          const unavailableLabel = label ? qs('[data-unavailable-label]', label) : null;

          // First option stays selectable (to view a sold-out color);
          // following options are disabled when unavailable.
          radio.disabled = !exists || (index > 0 && !available);
          label?.classList.toggle('is-unavailable', !available);
          if (unavailableLabel) unavailableLabel.hidden = available;
        });
      });
    }

    update({ initial = false } = {}) {
      const variant = this.currentVariant();
      const shown = variant || this.displayVariant();

      // Legends
      this.fieldsets.forEach((fieldset, i) => {
        const valueEl = qs('[data-selected-value]', fieldset);
        if (valueEl) valueEl.textContent = this.selected[i] || '';
      });

      if (this.idInput) {
        const nextId = variant ? String(variant.id) : '';
        if (this.idInput.value !== nextId) {
          this.idInput.value = nextId;
          // Lets third-party order forms (COD apps…) follow the selected variant.
          if (!initial) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      this.updatePrice(shown);
      this.updateInventory(variant);
      this.updateButtons(variant);
      this.updateSticky(variant, shown);

      if (shown && shown.media && this.gallery && typeof this.gallery.goToMedia === 'function' && !initial) {
        this.gallery.goToMedia(shown.media);
      }

      if (!initial && variant && this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }

      L.publish('variant:change', { sectionId: this.sectionId, variant });
    }

    updatePrice(variant) {
      const container = qs('[data-price-container]', this);
      if (!container || !variant) return;
      const current = qs('[data-price-current]', container);
      const compareWrap = qs('[data-price-compare-wrap]', container);
      const compare = qs('[data-price-compare]', container);
      const save = qs('[data-price-save]', container);
      const label = qs('[data-price-label]', container);
      const price = qs('.price', container);

      if (current) current.textContent = variant.price;
      if (compare) compare.textContent = variant.compare || '';
      if (compareWrap) compareWrap.hidden = !variant.compare;
      if (save) {
        save.textContent = variant.save || '';
        save.hidden = !variant.save;
      }
      price?.classList.toggle('price--on-sale', Boolean(variant.compare));
      if (label && this.priceLabels) label.textContent = variant.compare ? this.priceLabels.sale : this.priceLabels.regular;
    }

    get priceLabels() {
      const el = qs('[data-price-label]', this);
      return el ? { sale: el.dataset.sale, regular: el.dataset.regular } : null;
    }

    updateInventory(variant) {
      const el = qs('[data-inventory]', this);
      if (!el || !this.inventorySettings.enabled) return;
      const text = qs('[data-inventory-text]', el);
      const s = this.inventorySettings;
      let message = '';
      let state = '';

      if (variant) {
        if (variant.inventory !== null && variant.inventory > 0 && variant.inventory <= s.threshold) {
          message =
            variant.inventory === 1
              ? this.strings.lowStockOne
              : (this.strings.lowStockOther || '').replace('[count]', variant.inventory);
          state = 'is-low';
        } else if (variant.backorder && s.showBackorder) {
          message = this.strings.backorder;
        } else if (variant.available && s.showInStock) {
          message = this.strings.inStock;
          state = 'is-in-stock';
        }
      }

      el.classList.remove('is-low', 'is-in-stock');
      if (state) el.classList.add(state);
      if (text) text.textContent = message || '';
      el.hidden = !message;
    }

    updateButtons(variant) {
      const soldOut = Boolean(variant && !variant.available);
      this.submitButtons.forEach((button) => {
        const label = qs('[data-button-label]', button);
        button.disabled = soldOut;
        if (!label) return;
        if (soldOut) label.textContent = this.strings.soldOut;
        else label.innerHTML = button.dataset.labelHtml || label.innerHTML;
      });
      // Shopify's dynamic checkout button reads the form's variant id: keep it
      // unusable until a complete, available variant is chosen.
      const dynamic = this.form ? qs('[data-dynamic-checkout]', this.form) : null;
      if (dynamic) {
        const ready = Boolean(variant && variant.available);
        dynamic.toggleAttribute('inert', !ready);
        dynamic.classList.toggle('is-disabled', !ready);
      }
    }

    /* ---------- Validation ---------- */

    missingOptions() {
      return this.fieldsets.filter((_, i) => this.selected[i] === null);
    }

    showOptionError(fieldset) {
      const error = qs('.variant-picker__error', fieldset);
      const message = (this.strings.selectOption || '').replace('[option]', fieldset.dataset.optionName);
      fieldset.classList.add('is-invalid');
      if (error) {
        qs('[data-error-text]', error).textContent = message;
        error.hidden = false;
      }
      return message;
    }

    clearOptionError(fieldset) {
      fieldset.classList.remove('is-invalid');
      const error = qs('.variant-picker__error', fieldset);
      if (error) error.hidden = true;
    }

    validateSelection() {
      const missing = this.missingOptions();
      if (!missing.length) return true;
      const messages = missing.map((fieldset) => this.showOptionError(fieldset));
      const first = missing[0];
      first.scrollIntoView({ behavior: L.prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
      const firstRadio = qsa('input[type="radio"]', first).find((r) => !r.disabled);
      firstRadio?.focus({ preventScroll: true });
      L.announce(messages.join(' '), { assertive: true });
      return false;
    }

    /* ---------- Submit ---------- */

    async onSubmit(event) {
      event.preventDefault();
      if (this.pending) return;

      const submitter = event.submitter || this.lastSubmitter || this.submitButtons[0];
      this.hideMessages();
      if (!this.validateSelection()) return;

      const variant = this.currentVariant();
      if (!variant || !variant.available) return;

      const action = submitter?.dataset.submitAction || 'add';
      this.setPending(true, submitter);

      const formData = new FormData(this.form);
      formData.delete('return_to');
      formData.set('id', String(variant.id));
      if (!formData.get('quantity')) formData.set('quantity', '1');
      const cartSections = typeof L.cartSections === 'function' ? L.cartSections() : [];
      if (cartSections.length) {
        formData.append('sections', cartSections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(`${L.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          this.showError(data.description || data.message || this.strings.cartError);
          this.setPending(false, submitter);
          return;
        }

        if (action === 'checkout') {
          // Keep the busy state while the browser navigates to Shopify checkout.
          window.location.assign(L.routes.cart.replace(/cart\/?$/, 'checkout'));
          return;
        }

        const hasDrawer = Boolean(document.querySelector('cart-drawer'));
        if (action === 'cart' && !hasDrawer) {
          // "Ajouter et voir le panier" without a drawer: go to the cart page.
          window.location.assign(L.routes.cart);
          return;
        }

        L.publish('cart:add', { item: data, sections: data.sections, sectionId: this.sectionId, action, opener: this.externalOpener || submitter });
        this.refreshCart();
        L.announce(this.strings.added);
        // Without a cart drawer, confirm inline with a link to the cart.
        if (!hasDrawer) this.showSuccess();
      } catch (error) {
        console.error(error);
        this.showError(this.strings.networkError);
      }
      this.setPending(false, submitter);
    }

    async refreshCart() {
      try {
        const cart = await (await fetch(`${L.routes.cart}.js`, { headers: { Accept: 'application/json' } })).json();
        L.publish('cart:update', { cart, source: 'product-form' });
      } catch (error) {
        /* The add succeeded; a count refresh failure is not blocking. */
      }
    }

    setPending(pending, submitter) {
      this.pending = pending;
      if (pending) {
        this.submitButtons.forEach((button) => {
          button.disabled = true;
        });
        submitter?.setAttribute('aria-busy', 'true');
      } else {
        submitter?.removeAttribute('aria-busy');
        this.updateButtons(this.currentVariant());
      }
    }

    hideMessages() {
      const error = this.form && qs('[data-form-error]', this.form);
      const success = this.form && qs('[data-form-success]', this.form);
      if (error) error.hidden = true;
      if (success) success.hidden = true;
    }

    showError(message) {
      const error = this.form && qs('[data-form-error]', this.form);
      if (!error) return;
      qs('[data-form-error-text]', error).textContent = message;
      error.hidden = false;
      L.announce(message, { assertive: true });
    }

    showSuccess() {
      const success = this.form && qs('[data-form-success]', this.form);
      if (success) success.hidden = false;
    }

    /* ---------- Sticky bar (mobile) ---------- */

    setupSticky() {
      if (!this.sticky || !this.form) return;
      const buttons = qs('[data-buy-buttons]', this.form);
      if (!buttons) return;

      // Show only once the buttons are above the viewport. Checked on scroll
      // (rAF-throttled) rather than with IntersectionObserver, which misses
      // jumps that skip over the buttons (anchor links, restored scroll).
      let frame = null;
      const evaluate = () => {
        frame = null;
        const passed = buttons.getBoundingClientRect().bottom < 0;
        this.sticky.show(passed && !desktop.matches);
      };
      const schedule = () => {
        if (frame === null) frame = window.requestAnimationFrame(evaluate);
      };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      desktop.addEventListener('change', schedule);
      evaluate();

      qs('[data-sticky-submit]', this.sticky)?.addEventListener('click', () => {
        if (!this.validateSelection()) return;
        const primary = this.submitButtons[0];
        if (primary && !primary.disabled) this.form.requestSubmit(primary);
      });
    }

    updateSticky(variant, shown) {
      if (!this.sticky) return;
      const price = qs('[data-sticky-price]', this.sticky);
      const label = qs('[data-sticky-variant]', this.sticky);
      const button = qs('[data-sticky-submit]', this.sticky);
      if (price && shown) price.textContent = shown.price;
      if (label) {
        if (variant) label.textContent = variant.title === 'Default Title' ? '' : variant.title;
        else {
          const missing = this.missingOptions();
          const onlySize = missing.length === 1 && this.product.options[this.fieldsets.indexOf(missing[0])]?.type === 'size';
          label.textContent = onlySize ? this.strings.chooseSize : this.strings.chooseOptions;
        }
      }
      if (button) {
        const soldOut = variant && !variant.available;
        button.disabled = Boolean(soldOut);
        if (soldOut) {
          button.dataset.label = button.dataset.label || button.textContent.trim();
          button.textContent = this.strings.soldOut;
        } else if (button.dataset.label) {
          button.textContent = button.dataset.label;
        }
      }
    }

    /* ---------- Recently viewed (handles only; no personal data) ---------- */

    rememberProduct() {
      try {
        const handles = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]').filter((h) => h !== this.product.handle);
        handles.unshift(this.product.handle);
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(handles.slice(0, 12)));
      } catch (error) {
        /* Storage unavailable (private mode): feature silently disabled. */
      }
    }
  }

  customElements.define('product-info', ProductInfo);

  // Remember which submit button was used (for browsers without event.submitter).
  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-submit-action]');
    if (!button) return;
    const info = button.closest('product-info');
    if (info) info.lastSubmitter = button;
  });
})();
